import { useEffect, useRef, useState } from 'react';

/**
 * WebcamBackground - Displays webcam feed as background with audio on all outputs
 */
export function WebcamBackground({ useWebcam, webcamDeviceId, audioDeviceId }) {
  const videoRef = useRef(null);
  const extraAudioRefs = useRef([]);
  const [webcamStream, setWebcamStream] = useState(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [error, setError] = useState(null);
  const currentDeviceIdRef = useRef('');
  const currentAudioIdRef = useRef('');

  // Unlock audio on first user interaction with the page
  useEffect(() => {
    const unlock = () => {
      setAudioUnlocked(true);
      if (videoRef.current) {
        videoRef.current.muted = false;
      }
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Route audio to all available output devices (HDMI + speakers)
  const routeAudioToAllOutputs = async (stream) => {
    extraAudioRefs.current.forEach(el => {
      el.srcObject = null;
      el.remove();
    });
    extraAudioRefs.current = [];

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      
      if (outputs.length <= 1) return;

      const defaultId = outputs[0]?.deviceId || 'default';
      
      for (const output of outputs) {
        if (output.deviceId === defaultId || output.deviceId === 'default') continue;
        
        try {
          const audioEl = new Audio();
          audioEl.srcObject = stream;
          if (audioEl.setSinkId) {
            await audioEl.setSinkId(output.deviceId);
          }
          audioEl.play().catch(() => {});
          extraAudioRefs.current.push(audioEl);
        } catch (err) {
          console.warn('Could not route audio to:', output.label, err);
        }
      }
    } catch (err) {
      console.warn('Could not enumerate audio outputs:', err);
    }
  };

  useEffect(() => {
    const startWebcam = async () => {
      if (useWebcam && (!webcamStream || currentDeviceIdRef.current !== webcamDeviceId || currentAudioIdRef.current !== audioDeviceId)) {
        if (webcamStream) {
          webcamStream.getTracks().forEach(track => track.stop());
          setWebcamStream(null);
        }
        setError(null);
        
        try {
          const constraints = { 
            video: webcamDeviceId 
              ? { deviceId: { exact: webcamDeviceId }, width: { min: 1920, ideal: 1920 }, height: { min: 1080, ideal: 1080 } }
              : { width: { min: 1920, ideal: 1920 }, height: { min: 1080, ideal: 1080 } },
            audio: audioDeviceId
              ? { deviceId: { exact: audioDeviceId } }
              : true
          };
          
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          setWebcamStream(stream);
          setError(null);
          currentDeviceIdRef.current = webcamDeviceId;
          currentAudioIdRef.current = audioDeviceId;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            if (audioUnlocked) {
              videoRef.current.muted = false;
            }
          }
          routeAudioToAllOutputs(stream);
        } catch (err) {
          console.error('Webcam+audio error:', err);
          // Fallback: try without audio
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: webcamDeviceId 
                ? { deviceId: { exact: webcamDeviceId }, width: { min: 1920, ideal: 1920 }, height: { min: 1080, ideal: 1080 } }
                : { width: { min: 1920, ideal: 1920 }, height: { min: 1080, ideal: 1080 } },
              audio: false
            });
            setWebcamStream(stream);
            setError(null);
            currentDeviceIdRef.current = webcamDeviceId;
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            }
          } catch (err2) {
            console.error('Webcam error:', err2);
            setError(err2.name === 'NotAllowedError' ? 'Kameratilgang nektet. Tillat kamera i nettleseren.' 
              : err2.name === 'NotFoundError' ? 'Ingen kamera funnet. Koble til kamera og prøv igjen.'
              : `Kamerafeil: ${err2.message}`);
          }
        }
      } else if (!useWebcam && webcamStream) {
        webcamStream.getTracks().forEach(track => track.stop());
        setWebcamStream(null);
        setError(null);
        currentDeviceIdRef.current = '';
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
        extraAudioRefs.current.forEach(el => {
          el.srcObject = null;
          el.remove();
        });
        extraAudioRefs.current = [];
      }
    };
    
    startWebcam();
  }, [useWebcam, webcamDeviceId, audioDeviceId, webcamStream, audioUnlocked]);

  useEffect(() => {
    if (videoRef.current && webcamStream) {
      videoRef.current.srcObject = webcamStream;
    }
  }, [webcamStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webcamStream) {
        webcamStream.getTracks().forEach(track => track.stop());
      }
      extraAudioRefs.current.forEach(el => {
        el.srcObject = null;
        el.remove();
      });
      extraAudioRefs.current = [];
    };
  }, [webcamStream]);


  if (!useWebcam) return null;

  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
        }}
      />
      {/* Error message */}
      {error && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(220,38,38,0.9)',
            color: '#fff',
            padding: '16px 24px',
            borderRadius: '8px',
            zIndex: 100,
            fontFamily: "'Inter', sans-serif",
            fontSize: '1rem',
            textAlign: 'center',
            maxWidth: '400px',
          }}
          data-testid="webcam-error"
        >
          {error}
        </div>
      )}
      {/* Audio unlock prompt */}
      {!audioUnlocked && useWebcam && !error && (
        <div
          onClick={() => setAudioUnlocked(true)}
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#fff',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            zIndex: 100,
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.85rem',
          }}
        >
          Klikk for å aktivere lyd
        </div>
      )}
    </>
  );
}
